/**
 * Side panel editing the updates of ONE row (requirements F1.1–F1.6).
 *
 * Each update is a textarea that is always editable, with its mentions shown as
 * chips under it. Typing "@" opens the mention picker; picking inserts "@Name"
 * at the caret and records the mention. Order is the post order on apply, so the
 * cards can be moved up and down. The panel never saves anything itself: edits
 * go straight into the editor's draft through onChange, and the editor's Save
 * sends them with the rest of the row.
 */
import { Trash2 } from 'lucide-react';
import { IconButton } from 'monday-ui-react-core';
import { CloseSmall } from 'monday-ui-react-core/icons';
import React, { useEffect, useRef, useState } from 'react';
import { isBlankUpdate } from '../../lib/rowNames';
import { emptyUpdate, mentionText, pruneMentions, withMention } from '../../lib/updates';
import MentionPicker from './MentionPicker';
import './UpdatesPanel.css';

/** "@que" right before the caret, or null. */
function mentionQueryAt(text, caret) {
  const before = text.slice(0, caret);
  const m = /(^|\s)@([^\s@]*)$/.exec(before);
  if (!m) return null;
  return { start: before.length - m[2].length - 1, end: caret, query: m[2] };
}

// showErrors: a save was refused over empty updates — outline them until written or deleted
export default function UpdatesPanel({ row, limits, people, showErrors = false, onChange, onClose }) {
  const updates = row.updates || [];
  const [picker, setPicker] = useState(null); // { index, start, end, query, anchorEl }
  const areaRefs = useRef({});
  const atLimit = updates.length >= limits.updatesPerRow;

  // Escape closes the panel (the picker, when open, takes the first Escape itself).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !picker) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, picker]);

  const setUpdate = (index, next) => onChange(updates.map((u, i) => (i === index ? next : u)));

  const onBodyChange = (index, e) => {
    const body = e.target.value;
    setUpdate(index, pruneMentions({ ...updates[index], body }));
    const q = mentionQueryAt(body, e.target.selectionStart);
    if (q) {
      people.load();
      setPicker({ index, ...q, anchorEl: e.target });
    } else if (picker) {
      setPicker(null);
    }
  };

  const pick = (mention) => {
    if (!picker) return;
    const u = updates[picker.index];
    const body = u.body || '';
    const text = mentionText(mention);
    const nextBody = `${body.slice(0, picker.start)}${text} ${body.slice(picker.end)}`;
    setUpdate(picker.index, withMention({ ...u, body: nextBody }, mention));
    const caret = picker.start + text.length + 1;
    const area = areaRefs.current[picker.index];
    setPicker(null);
    if (area) {
      requestAnimationFrame(() => {
        area.focus();
        area.setSelectionRange(caret, caret);
      });
    }
  };

  const removeMention = (index, mention) => {
    const u = updates[index];
    setUpdate(index, { ...u, mentions: (u.mentions || []).filter((m) => m !== mention) });
  };

  const remove = (index) => onChange(updates.filter((_, i) => i !== index));
  const add = () => {
    if (atLimit) return;
    onChange([...updates, emptyUpdate()]);
  };

  return (
    <aside className="spt-updates-panel" role="dialog" aria-label={`Updates for ${row.name || 'this subitem'}`}>
      <header className="spt-updates-head">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-primary truncate">
            Updates · {row.name || 'Untitled subitem'}
          </div>
          <div className="text-xs text-secondary">
            {updates.length} of {limits.updatesPerRow}
          </div>
        </div>
        <IconButton size={IconButton.sizes.SMALL} icon={CloseSmall} ariaLabel="Close updates" onClick={onClose} />
      </header>

      <div className="spt-updates-list">
        {updates.map((u, index) => {
          const body = u.body || '';
          const over = body.length > limits.updateBodyChars;
          const empty = showErrors && isBlankUpdate(u);
          return (
            <div key={index} className={`spt-update-card${empty ? ' is-invalid' : ''}`}>
              <textarea
                ref={(el) => {
                  areaRefs.current[index] = el;
                }}
                className="spt-update-body"
                value={body}
                placeholder="Write an update. Type @ to mention someone."
                aria-label={`Update ${index + 1}`}
                aria-invalid={empty || undefined}
                onChange={(e) => onBodyChange(index, e)}
                onClick={(e) => {
                  const q = mentionQueryAt(e.target.value, e.target.selectionStart);
                  if (!q && picker) setPicker(null);
                }}
              />
              {empty && (
                <p className="spt-update-error" role="alert">
                  Write this update or delete it.
                </p>
              )}
              {(u.mentions || []).length > 0 && (
                <div className="spt-mention-row">
                  {(u.mentions || []).map((m) => (
                    <span key={`${m.type}:${m.id || m.name}`} className="spt-mention-chip">
                      {mentionText(m)}
                      <button
                        type="button"
                        className="spt-mention-x"
                        aria-label={`Stop mentioning ${m.name}`}
                        title="Keep the text, drop the notification"
                        onClick={() => removeMention(index, m)}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="spt-update-meta">
                <span className={over ? 'text-danger-ink font-semibold' : undefined}>
                  {body.length.toLocaleString()} / {limits.updateBodyChars.toLocaleString()}
                </span>
                <span className="flex-1" />
                <button type="button" className="spt-update-act spt-update-del" onClick={() => remove(index)} aria-label="Delete update">
                  <Trash2 className="w-3.5 h-3.5" />
                  Delete
                </button>
              </div>
            </div>
          );
        })}

        <button
          type="button"
          className="spt-update-add"
          onClick={add}
          disabled={atLimit}
          title={atLimit ? `A subitem can carry up to ${limits.updatesPerRow} updates` : undefined}
        >
          + Add update
        </button>
      </div>

      <footer className="spt-updates-foot">
        Posted in this order when the template is applied.
      </footer>

      {picker && (
        <MentionPicker
          anchorEl={picker.anchorEl}
          query={picker.query}
          people={people}
          onPick={pick}
          onClose={() => setPicker(null)}
        />
      )}
    </aside>
  );
}
